import { useEffect, useRef } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import * as Sentry from "@sentry/react";
import {
  migrateLegacyGuestStorage,
  getStoredGuestSession,
} from "../../services/guestStorage";
import { createGuestSession } from "../../services/guestSessionService";

/**
 * Boot-time guest-session self-heal.
 *
 * Guest JWTs are scoped to the API backend that minted them (per-stack
 * OMMUSE_SHARE_SECRET). A token from another backend is rejected and lands in
 * the server's `s2s == false` guards as
 *   PERMISSION_DENIED "user id must match request".
 *
 * Every guest created while app.ommuse.com was mis-routed to the dev API holds
 * such a token under the old, un-namespaced localStorage keys. On boot we:
 *   1. purge those legacy keys (migrateLegacyGuestStorage), and
 *   2. if the visitor had a *live* legacy guest session and isn't signed in and
 *      has no valid current-backend session, auto-start a fresh guest so they
 *      keep browsing seamlessly (the old guest data lived on the other backend
 *      and is unreachable regardless).
 *
 * Runs exactly once, after Auth0 resolves. Anonymous first-time visitors (no
 * legacy session) are left untouched — we never auto-guest someone who wasn't
 * already one.
 */
export const useGuestSessionHealth = (): void => {
  const { isLoading, isAuthenticated } = useAuth0();
  const ranRef = useRef(false);

  useEffect(() => {
    if (isLoading || ranRef.current) return;
    ranRef.current = true;

    const { hadLiveLegacySession } = migrateLegacyGuestStorage();

    // Signed-in users never carry a guest session; legacy keys are just purged.
    if (isAuthenticated) return;
    // A valid session for the current backend already exists — nothing to heal.
    if (getStoredGuestSession()) return;
    // Only re-guest a visitor who actually had a live guest session.
    if (!hadLiveLegacySession) return;

    createGuestSession().catch((e) => Sentry.captureException(e));
  }, [isLoading, isAuthenticated]);
};

export default useGuestSessionHealth;
